import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRole } from '../../types';
import { usersService, UserFilters, UserDetail } from '../../services/api/users';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import UserFormModal from './UserFormModal';
import UserActionsModal from './UserActionsModal';
import './UserList.css';

const UserList: React.FC = () => {
  const { t } = useTranslation();

  // Data state
  const [users, setUsers] = useState<UserDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Modals
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isActionsModalOpen, setIsActionsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDetail | null>(null);
  const [selectedUserForActions, setSelectedUserForActions] = useState<UserDetail | null>(null);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: UserFilters = {
        page,
        pageSize,
        q: debouncedSearch || undefined,
        role: roleFilter as UserRole || undefined,
        status: statusFilter as 'active' | 'inactive' || undefined,
      };
      const response = await usersService.getAll(filters);
      setUsers(response.items);
      setTotal(response.total);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = () => {
    setEditingUser(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (user: UserDetail) => {
    setEditingUser(user);
    setIsFormModalOpen(true);
  };

  const handleActions = (user: UserDetail) => {
    setSelectedUserForActions(user);
    setIsActionsModalOpen(true);
  };

  const getRoleBadgeClass = (role: string) => {
    const roleLower = role.toLowerCase().replace('_', '_');
    return `role-badge ${roleLower}`;
  };

  const formatLastLogin = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalPages = Math.ceil(total / pageSize);
  const roles = Object.values(UserRole);

  if (error && users.length === 0) {
    return <ErrorMessage error={error} onRetry={fetchUsers} />;
  }

  return (
    <div className="user-list-page">
      <div className="page-header">
        <h1>{t('users.title')}</h1>
        <button className="button button-primary" onClick={handleCreate}>
          + {t('users.create')}
        </button>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          className="search-input"
          placeholder={t('common.search') + '...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="filter-select"
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('users.role')}: All</option>
          {roles.map((role) => (
            <option key={role} value={role}>
              {t(`roles.${role.toLowerCase()}`)}
            </option>
          ))}
        </select>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('common.status')}: All</option>
          <option value="active">{t('status.active')}</option>
          <option value="inactive">{t('status.inactive')}</option>
        </select>
        <span className="results-count">
          {total} {t('users.title').toLowerCase()}
        </span>
      </div>

      {loading && users.length === 0 ? (
        <Loading text={t('common.loading')} />
      ) : users.length === 0 ? (
        <div className="empty-state">
          <p>{t('common.noData')}</p>
          <button className="button button-primary" onClick={handleCreate}>
            {t('users.create')}
          </button>
        </div>
      ) : (
        <>
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('users.email')}</th>
                  <th>{t('users.role')}</th>
                  <th>{t('users.employee')}</th>
                  <th>{t('users.isActive')}</th>
                  <th>{t('users.lastLogin')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={getRoleBadgeClass(user.role)}>
                        {t(`roles.${user.role.toLowerCase()}`)}
                      </span>
                    </td>
                    <td>
                      {user.employee ? (
                        <span>
                          {user.employee.fullName} ({user.employee.employeeCode})
                        </span>
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? t('status.active') : t('status.inactive')}
                      </span>
                    </td>
                    <td>{formatLastLogin((user as any).lastLoginAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="button-link" onClick={() => handleEdit(user)}>
                          {t('common.edit')}
                        </button>
                        <button
                          className="button-link warning"
                          onClick={() => handleActions(user)}
                        >
                          {t('common.actions')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="button button-secondary button-small"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="button button-secondary button-small"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Form Modal */}
      <UserFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingUser(null);
        }}
        onSuccess={fetchUsers}
        user={editingUser}
      />

      {/* Actions Modal */}
      <UserActionsModal
        isOpen={isActionsModalOpen}
        onClose={() => {
          setIsActionsModalOpen(false);
          setSelectedUserForActions(null);
        }}
        onSuccess={fetchUsers}
        user={selectedUserForActions}
      />
    </div>
  );
};

export default UserList;
